import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { AlertTriangle, ShieldCheck, Wrench } from 'lucide-react'
import { FadedPhoto } from '../components/brand/FadedPhoto.jsx'
import { AppleIcon, DiscordIcon, LogoMark } from '../components/brand/Logo.jsx'
import { Avatar } from '../components/ui/Avatar.jsx'
import { Button, buttonClasses } from '../components/ui/Button.jsx'
import { Card, Container, Eyebrow, Pill } from '../components/ui/primitives.jsx'
import { api } from '../lib/api.js'
import { pickBackground } from '../lib/backgrounds.js'
import { appleSignInCode, isNativeApp } from '../lib/native.js'
import { useSession } from '../lib/session.jsx'

const ERRORS = {
  cancelled: 'Sign-in was cancelled on Discord.',
  state_mismatch: 'That sign-in link expired. Please try again.',
  missing_code: 'Discord didn’t send us a sign-in code. Please try again.',
  discord_failed: 'We couldn’t reach Discord just now. Please try again in a moment.',
  discord_unavailable: 'Discord sign-in isn’t configured on this server yet.',
  account_deleted: 'This account was deleted.',
  apple_cancelled: 'Sign-in was cancelled on Apple.',
  apple_failed: 'We couldn’t reach Apple just now. Please try again in a moment.',
  apple_unavailable: 'Sign in with Apple isn’t switched on yet.',
  rate_limited: 'Too many sign-in attempts. Wait a few minutes and try again.',
}

function safeNext(value) {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/'
}

function DevLogin({ next }) {
  const [users, setUsers] = useState(null)
  const [busy, setBusy] = useState(null)
  const { refresh } = useSession()
  const navigate = useNavigate()

  useEffect(() => {
    api('/api/auth/dev-users')
      .then((data) => setUsers(data.users))
      .catch(() => setUsers([]))
  }, [])

  async function signInAs(id) {
    setBusy(id)
    try {
      await api('/auth/dev-login', { method: 'POST', body: { userId: id } })
      await refresh()
      navigate(next)
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2">
        <Wrench aria-hidden="true" className="size-4 text-secondary-400" />
        <h2 className="font-medium">Dev login</h2>
        <Pill tone="secondary" className="ml-auto">
          Local only
        </Pill>
      </div>
      <p className="mt-1 text-sm text-ink-muted">Sign in as a seeded user without Discord. Disabled in production.</p>
      <ul className="mt-4 max-h-80 space-y-1 overflow-y-auto pr-1">
        {(users ?? []).slice(0, 16).map((user) => (
          <li key={user.id}>
            <button
              type="button"
              onClick={() => signInAs(user.id)}
              disabled={busy !== null}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm hover:bg-white/5 disabled:opacity-60"
            >
              <Avatar src={user.avatarUrl} name={user.displayName} size="sm" decorative />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{user.displayName}</span>
                {user.orgs && <span className="block truncate text-xs text-ink-muted">{user.orgs}</span>}
              </span>
              {user.isPlatformAdmin && <Pill tone="primary">Admin</Pill>}
            </button>
          </li>
        ))}
      </ul>
    </Card>
  )
}

// Continue with Apple: Apple's own sheet in the iOS app, Apple's sign-in
// page on the web.
function AppleButton({ next }) {
  const { meta, refresh } = useSession()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const native = isNativeApp()
  if (!(native ? meta?.auth.appleNative : meta?.auth.apple)) return null
  const className = buttonClasses({ variant: 'apple', size: 'lg', className: 'w-full' })
  if (!native) {
    return (
      <a href={`/auth/apple${next === '/' ? '' : `?next=${encodeURIComponent(next)}`}`} className={className}>
        <AppleIcon /> Continue with Apple
      </a>
    )
  }
  async function signIn() {
    setBusy(true)
    setError(null)
    try {
      const code = await appleSignInCode()
      if (!code) return
      const res = await api('/auth/apple/native', { method: 'POST', body: { code, next } })
      await refresh()
      navigate(res.next)
    } catch (err) {
      setError(ERRORS[err.code] ?? err.message ?? 'Sign-in failed. Please try again.')
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <button type="button" onClick={signIn} disabled={busy} className={className}>
        <AppleIcon /> {busy ? 'Signing in…' : 'Continue with Apple'}
      </button>
      {error && (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      )}
    </>
  )
}

export default function SignInPage() {
  const [params] = useSearchParams()
  const { user, meta } = useSession()
  const next = safeNext(params.get('next'))
  const error = params.get('error')
  const discordHref = `/auth/discord${next === '/' ? '' : `?next=${encodeURIComponent(next)}`}`
  const photo = pickBackground('pages', 'signin')

  return (
    <div className="relative isolate">
      <title>Sign in · Pred Hub</title>
      <div aria-hidden="true" className="bg-landing absolute inset-0 -z-30 [mask-image:linear-gradient(to_bottom,black_60%,transparent)]" />
      {photo && <FadedPhoto src={photo.src} position={photo.position} side="top" strength="subtle" className="-z-20" />}
      <div aria-hidden="true" className="dot-grid absolute inset-0 -z-10 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
      <Container className="grid max-w-5xl gap-6 py-16 sm:py-24 md:grid-cols-[1fr_auto] md:items-start">
        <Card className="mx-auto w-full max-w-md p-8">
          <LogoMark className="size-12" />
          <Eyebrow className="mt-6">Welcome</Eyebrow>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-wide uppercase">Sign in to Pred Hub</h1>
          <p className="mt-2 text-ink-muted">Sign in with Discord, where most of the scene lives, or with Apple.</p>

          {error && (
            <div role="alert" className="mt-6 flex gap-2.5 rounded-xl bg-red-500/10 p-3 text-sm text-red-200 ring-1 ring-red-500/25 ring-inset">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
              {ERRORS[error] ?? 'Sign-in failed. Please try again.'}
            </div>
          )}

          {user ? (
            <div className="mt-6 space-y-3">
              <p className="text-sm">
                You’re signed in as <strong>{user.displayName}</strong>.
              </p>
              <Button as={Link} to={next} className="w-full">
                Continue
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <a
                href={discordHref}
                aria-disabled={meta && !meta.auth.discord ? 'true' : undefined}
                className={buttonClasses({ variant: 'discord', size: 'lg', className: 'w-full' })}
              >
                <DiscordIcon /> Continue with Discord
              </a>
              <AppleButton next={next} />
              <p className="text-center text-xs text-ink-muted">
                By continuing, you agree to the{' '}
                <Link to="/terms" className="rounded font-medium text-ink underline-offset-2 hover:underline">
                  Terms of Use
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="rounded font-medium text-ink underline-offset-2 hover:underline">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          )}

          <div className="mt-6 flex gap-2.5 rounded-xl bg-white/[0.03] p-3 text-xs text-ink-muted ring-1 ring-white/5 ring-inset">
            <ShieldCheck aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-emerald-300" />
            <p>
              From Discord we only see your username and avatar; from Apple, not even your name or email. We never read
              your servers or messages, and we never post as you.
            </p>
          </div>
        </Card>
        {meta?.auth.devLogin && !user && (
          <div className="mx-auto w-full max-w-md md:w-80">
            <DevLogin next={next} />
          </div>
        )}
      </Container>
    </div>
  )
}
